import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FileText,
  MessageSquare,
  Shield,
  FolderOpen,
  ListChecks,
  Route,
  Info,
} from "lucide-react";
import StickyHeader from "../../../components/ModuleCommon/StickyHeader/StickyHeader";
import { ExpandableHeader } from "../../../components/ACS-Dashboard/ACS-Blocks/ExpandableHeader";
import { DisplayDetail } from "../../../components/ACS-Dashboard/ACS-Blocks/DisplayDetail";
import ScrollbarWrapper from "../../../components/ACS-Dashboard/ACS-Blocks/ScrollbarWrapper";
import Loader from "../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import Button from "../../../components/ACS-Dashboard/ACS-Blocks/Button";
import notify from "../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import { carrierSourcingApi } from "./api/carrierSourcingApi";
import {
  apiErrorMessage,
  mapCarrierDetails,
  mapCarrierDocument,
  mapCompliance,
  mapEntry,
  mapLaneRate,
  mapOnboardingItem,
  mapRateDocument,
  mapRateVersion,
  mapThread,
  riskLabel,
} from "./mappers";
import {
  Chip,
  NoteFlag,
  OnboardProgress,
  RateOnboardChip,
  RiskChip,
  TimelineItem,
} from "./ui";
import UploadDocumentModal from "./UploadDocumentModal";
import ManualVerifyModal from "./ManualVerifyModal";
import LaneRatesGrid from "./LaneRatesGrid";

/** Hover/focus tooltip explaining compliance risk score + tier thresholds. */
function RiskScoringInfo() {
  return (
    <span className="group/risk-tip relative inline-flex items-center">
      <button
        type="button"
        className="inline-flex text-gray-400 hover:text-[#223688] focus:outline-none focus-visible:text-[#223688]"
        aria-label="How risk scoring works"
        aria-describedby="compliance-risk-scoring-tip"
      >
        <Info size={15} strokeWidth={2} aria-hidden />
      </button>
      <span
        id="compliance-risk-scoring-tip"
        role="tooltip"
        className="pointer-events-none invisible absolute left-0 top-[calc(100%+8px)] z-[200] w-[min(22rem,calc(100vw-2.5rem))] rounded-lg border border-gray-300 bg-white p-3 text-left text-dense-xs leading-5 text-gray-800 shadow-lg group-hover/risk-tip:visible group-focus-within/risk-tip:visible dark:border-acs-dark-border dark:bg-acs-dark-surface dark:text-acs-dark-text sm:left-auto sm:right-0"
      >
        <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-[#223688]">
          How risk scoring works
        </p>
        <p className="mb-2">
          Score starts at <strong>70</strong> (0–100), then adjusts from the latest
          compliance checks:
        </p>
        <ul className="mb-2 list-disc space-y-0.5 pl-4">
          <li>FMCSA pass: +10</li>
          <li>FMCSA fail: −25</li>
          <li>FMCSA manual / pending: −5</li>
          <li>Insurance expired: −25</li>
        </ul>
        <p className="mb-1.5 font-semibold text-gray-700 dark:text-acs-dark-text">
          Risk tiers
        </p>
        <ul className="list-disc space-y-0.5 pl-4">
          <li>
            <strong>Low</strong> (80–100): stronger compliance posture
          </li>
          <li>
            <strong>Medium</strong> (60–79): moderate risk; typical starting band
          </li>
          <li>
            <strong>High</strong> (40–59): elevated compliance risk
          </li>
          <li>
            <strong>Critical</strong> (0–39): highest risk; needs attention
          </li>
        </ul>
      </span>
    </span>
  );
}

function SectionGrid({ children }) {
  return (
    <div className="grid grid-cols-1 gap-x-fluid-2 gap-y-fluid-3 bg-[#F9FAFE] p-fluid-3 dark:bg-acs-dark-surface sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      {children}
    </div>
  );
}

function StaticSection({ title, children }) {
  return (
    <div className="mb-fluid-3 overflow-hidden rounded-md border border-gray-300 bg-white dark:border-acs-dark-border dark:bg-acs-dark-surface">
      <div className="bg-[#1e2a74] dark:bg-blue-900/40 px-fluid-2 py-fluid-1.5 text-fluid-base font-medium text-white">
        {title}
      </div>
      {children}
    </div>
  );
}

export default function CarrierProfile({
  carrierId,
  onLogActivity,
  onEditProfile,
  onAddRate,
  refreshKey = 0,
}) {
  const [carrier, setCarrier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [commQuery, setCommQuery] = useState("");
  const [refreshingCompliance, setRefreshingCompliance] = useState(false);
  const [uploadingInsurance, setUploadingInsurance] = useState(false);
  const [uploadModal, setUploadModal] = useState(null); // "carrier" | "rate" | null
  const [extractingDocId, setExtractingDocId] = useState(null);
  const [verifyCheck, setVerifyCheck] = useState(null);
  const insuranceFileInputRef = useRef(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!carrierId) return;
    if (!silent) {
      setLoading(true);
    }
    setError("");
    try {
      const id = Number(carrierId);
      const detailsRes = await carrierSourcingApi.getCarrier(id);
      let profile = mapCarrierDetails(detailsRes.data);

      const [
        docsRes,
        versionsRes,
        threadsRes,
        carrierDocsRes,
        onboardRes,
        complianceRes,
        scoreRes,
      ] = await Promise.allSettled([
        carrierSourcingApi.listRateDocuments(id),
        carrierSourcingApi.listRateVersions(id),
        carrierSourcingApi.listThreads(id),
        carrierSourcingApi.listDocuments(id),
        carrierSourcingApi.getCarrierOnboarding(id),
        carrierSourcingApi.getCompliance(id),
        carrierSourcingApi.getCarrierScore(id),
      ]);

      if (docsRes.status === "fulfilled") {
        profile.rateDocuments = (docsRes.value.data || []).map(mapRateDocument);
      }
      if (versionsRes.status === "fulfilled") {
        const versions = (versionsRes.value.data || []).map(mapRateVersion);
        profile.rateVersions = versions;
        const active =
          versions.find((v) => v.status === "ACTIVE") ||
          versions.find((v) => v.status === "DRAFT") ||
          versions[0];
        if (active?.rawId) {
          try {
            const lanesRes = await carrierSourcingApi.listLaneRates(
              id,
              active.rawId
            );
            profile.laneRates = (lanesRes.data || []).map(mapLaneRate);
            profile.activeLanes = profile.laneRates.filter(
              (r) => r.rateOnboarding === "active"
            ).length;
            profile.overview.openLanes = profile.laneRates.length
              ? `${profile.laneRates.length} lane(s)`
              : "None yet";
            profile.negotiationNotes = active.notes || profile.notes || "";
          } catch {
            /* ignore lane load errors */
          }
        }
      }
      if (threadsRes.status === "fulfilled") {
        const threads = (threadsRes.value.data || []).map(mapThread);
        const withMessages = await Promise.all(
          threads.map(async (t) => {
            try {
              const entriesRes = await carrierSourcingApi.listThreadEntries(
                id,
                t.rawId
              );
              return {
                ...t,
                messages: (entriesRes.data || []).map(mapEntry),
                meta: `${(entriesRes.data || []).length} messages · ${t.meta}`,
              };
            } catch {
              return t;
            }
          })
        );
        profile.threads = withMessages;
        const firstMsg = withMessages.find((t) => t.messages?.length)?.messages?.[0];
        if (firstMsg) profile.recentActivity = firstMsg;
      }
      if (carrierDocsRes.status === "fulfilled") {
        profile.documents = {
          carrierLevel: (carrierDocsRes.value.data || []).map(mapCarrierDocument),
          rateSpecific: [],
        };
      }
      if (onboardRes.status === "fulfilled") {
        profile.carrierOnboarding = (onboardRes.value.data || [])
          .map(mapOnboardingItem)
          .sort((a, b) => a.sortOrder - b.sortOrder);
      }
      if (complianceRes.status === "fulfilled") {
        profile.compliance = mapCompliance(complianceRes.value.data);
        if (profile.compliance?.riskScore != null) {
          profile.riskScore = profile.compliance.riskScore;
        }
        if (profile.compliance?.riskTier) {
          profile.riskApi = profile.compliance.riskTier;
        }
      }
      if (scoreRes.status === "fulfilled") {
        const score = scoreRes.value.data?.score ?? scoreRes.value.data?.carrierScore;
        if (score != null) profile.score = Number(score);
      }

      setCarrier(profile);
    } catch (err) {
      setError(apiErrorMessage(err, "Failed to load carrier"));
      setCarrier(null);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [carrierId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!refreshKey) return;
    load({ silent: true });
  }, [refreshKey, load]);

  const threads = useMemo(() => {
    const list = carrier?.threads || [];
    const needle = commQuery.trim().toLowerCase();
    if (!needle) return list;
    return list
      .map((t) => ({
        ...t,
        messages: (t.messages || []).filter(
          (m) =>
            m.who.toLowerCase().includes(needle) ||
            m.txt.toLowerCase().includes(needle) ||
            t.laneName.toLowerCase().includes(needle)
        ),
      }))
      .filter(
        (t) =>
          t.messages.length > 0 || t.laneName.toLowerCase().includes(needle)
      );
  }, [carrier, commQuery]);

  const handleRefreshCompliance = async () => {
    if (!carrier?.rawId) return;
    setRefreshingCompliance(true);
    try {
      const res = await carrierSourcingApi.refreshCompliance(carrier.rawId);
      setCarrier((prev) =>
        prev
          ? {
              ...prev,
              compliance: mapCompliance(res.data),
              riskScore: res.data?.riskScore?.score ?? prev.riskScore,
              riskApi: res.data?.riskScore?.riskTier || prev.riskApi,
            }
          : prev
      );
    } catch (err) {
      setError(apiErrorMessage(err, "Compliance refresh failed"));
    } finally {
      setRefreshingCompliance(false);
    }
  };

  const applyComplianceSummary = (summary) => {
    setCarrier((prev) =>
      prev
        ? {
            ...prev,
            compliance: mapCompliance(summary),
            riskScore: summary?.riskScore?.score ?? prev.riskScore,
            riskApi: summary?.riskScore?.riskTier || prev.riskApi,
            risk: summary?.riskScore?.riskTier
              ? riskLabel(summary.riskScore.riskTier)
              : prev.risk,
          }
        : prev
    );
  };

  const handleUploadInsurance = async (event) => {
    const file = event.target.files?.[0];
    // Allow re-selecting the same file later
    event.target.value = "";
    if (!file || !carrier?.rawId) return;

    setUploadingInsurance(true);
    setError("");
    try {
      const res = await carrierSourcingApi.uploadInsuranceDocument(
        carrier.rawId,
        file
      );
      applyComplianceSummary(res.data);
      // Refresh document list so COI appears under Documents too
      try {
        const docsRes = await carrierSourcingApi.listDocuments(carrier.rawId);
        setCarrier((prev) =>
          prev
            ? {
                ...prev,
                documents: {
                  ...prev.documents,
                  carrierLevel: (docsRes.data || []).map(mapCarrierDocument),
                },
              }
            : prev
        );
      } catch {
        /* optional */
      }
      const mapped = mapCompliance(res.data);
      const ins = mapped?.insurance;
      if (ins?.identityMatched === true) {
        notify.success({
          title: "Insurance validated",
          message:
            ins.matchReason ||
            `Policy ${ins.policyStartDate || "—"} → ${ins.policyEndDate || "—"}`,
        });
      } else {
        notify.error({
          title: "Insurance identity mismatch",
          message:
            ins?.matchReason ||
            "MC number or insured name did not match this carrier profile.",
        });
      }
    } catch (err) {
      const msg = apiErrorMessage(err, "Insurance upload failed");
      setError(msg);
      notify.error({ title: "Insurance upload failed", message: msg });
    } finally {
      setUploadingInsurance(false);
    }
  };

  const handleManualVerify = async ({ status, notes }) => {
    if (!carrier?.rawId || !verifyCheck?.rawId) return;
    try {
      const res = await carrierSourcingApi.verifyComplianceCheck(
        carrier.rawId,
        verifyCheck.rawId,
        { status, notes }
      );
      setCarrier((prev) =>
        prev
          ? {
              ...prev,
              compliance: mapCompliance(res.data),
              riskScore: res.data?.riskScore?.score ?? prev.riskScore,
              riskApi: res.data?.riskScore?.riskTier || prev.riskApi,
            }
          : prev
      );
      notify.success({
        title: status === "PASS" ? "Marked verified" : "Marked failed",
        message: `${verifyCheck.title} updated after manual review.`,
      });
      setVerifyCheck(null);
    } catch (err) {
      const message = apiErrorMessage(err, "Manual verification failed");
      notify.error({ title: "Verification failed", message });
      throw new Error(message);
    }
  };

  const handleStartExtraction = async (document) => {
    if (!carrier?.rawId || !document?.rawId) return;
    const identityOk = carrier?.compliance?.insurance?.identityMatched === true;
    if (!identityOk) {
      notify.error({
        title: "Cannot extract rates",
        message:
          carrier?.compliance?.insurance?.matchReason ||
          "Neither MC number nor carrier name matches this carrier. Upload a matching COI under Compliance first.",
      });
      return;
    }
    setExtractingDocId(document.id);
    try {
      const res = await carrierSourcingApi.extractRateDocument(
        carrier.rawId,
        document.rawId
      );
      const result = res.data || {};
      const updated = mapRateDocument({
        id: document.rawId,
        fileName: document.name,
        status: result.status || "EXTRACTED",
        uploadedDate: document.uploaded,
        extractionError: result.extractionError,
        extractedAt: result.extractedAt,
      });
      setCarrier((prev) =>
        prev
          ? {
              ...prev,
              rateDocuments: (prev.rateDocuments || []).map((d) =>
                d.id === document.id ? { ...d, ...updated } : d
              ),
            }
          : prev
      );
      const errText = String(result.extractionError || "");
      const identityFailed =
        result.status === "FAILED" &&
        /identity|mc number|carrier name|insurance certificate|coi/i.test(errText);
      if (result.status === "FAILED" || identityFailed) {
        notify.error({
          title: identityFailed ? "Identity does not match" : "Extraction failed",
          message: result.extractionError || "OCR could not extract lane rates",
        });
      } else {
        notify.success({
          title: "Extraction complete",
          message: `Created ${result.lanesCreated ?? 0} lane rate(s) from ${document.name}.`,
        });
        await load();
      }
    } catch (err) {
      notify.error({
        title: "Cannot extract rates",
        message: apiErrorMessage(
          err,
          "Carrier identity must match (MC number or carrier name) before extracting rates."
        ),
      });
    } finally {
      setExtractingDocId(null);
    }
  };

  const handleUploadDocuments = async ({ files, documentType, documentVersion, expiryDate }) => {
    if (!carrier?.rawId || !files?.length) return;
    const id = carrier.rawId;
    const kind = uploadModal;
    try {
      if (kind === "rate") {
        for (const file of files) {
          await carrierSourcingApi.uploadRateDocument(id, file);
        }
        const docsRes = await carrierSourcingApi.listRateDocuments(id);
        setCarrier((prev) =>
          prev
            ? {
                ...prev,
                rateDocuments: (docsRes.data || []).map(mapRateDocument),
              }
            : prev
        );
      } else if (String(documentType || "").toUpperCase() === "COI") {
        // COI goes through compliance insurance OCR (MC / insured name + dates).
        let lastSummary = null;
        for (const file of files) {
          const res = await carrierSourcingApi.uploadInsuranceDocument(id, file);
          lastSummary = res.data;
        }
        if (lastSummary) {
          applyComplianceSummary(lastSummary);
        }
        const docsRes = await carrierSourcingApi.listDocuments(id);
        setCarrier((prev) =>
          prev
            ? {
                ...prev,
                documents: {
                  ...(prev.documents || {}),
                  carrierLevel: (docsRes.data || []).map(mapCarrierDocument),
                },
              }
            : prev
        );
        const ins = mapCompliance(lastSummary)?.insurance;
        if (ins?.identityMatched === true) {
          notify.success({
            title: "Insurance validated",
            message:
              ins.matchReason ||
              `Policy ${ins.policyStartDate || "—"} → ${ins.policyEndDate || "—"}`,
          });
        } else {
          notify.error({
            title: "Insurance identity mismatch",
            message:
              ins?.matchReason ||
              "MC number or insured name did not match this carrier profile.",
          });
        }
        setUploadModal(null);
        return;
      } else {
        for (const file of files) {
          await carrierSourcingApi.uploadDocument(id, file, {
            documentType,
            documentVersion: documentVersion || 1,
            expiryDate,
          });
        }
        const docsRes = await carrierSourcingApi.listDocuments(id);
        setCarrier((prev) =>
          prev
            ? {
                ...prev,
                documents: {
                  ...(prev.documents || {}),
                  carrierLevel: (docsRes.data || []).map(mapCarrierDocument),
                },
              }
            : prev
        );
      }
      notify.success({
        title: "Upload complete",
        message:
          files.length === 1
            ? "Document uploaded successfully."
            : `${files.length} documents uploaded successfully.`,
      });
      setUploadModal(null);
    } catch (err) {
      const message = apiErrorMessage(err, "Upload failed");
      notify.error({ title: "Upload failed", message });
      throw new Error(message);
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader message="Loading carrier..." />
      </div>
    );
  }

  if (error && !carrier) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6">
        <p className="text-fluid-sm text-red-600">{error}</p>
        <Button text="Retry" onClick={load} />
      </div>
    );
  }

  if (!carrier) {
    return (
      <div className="flex h-full items-center justify-center text-fluid-sm text-gray-500">
        Carrier not found.
      </div>
    );
  }

  const insuranceIdentityMatched =
    carrier.compliance?.insurance?.identityMatched === true;
  const identityMatchError = insuranceIdentityMatched
    ? ""
    : carrier.compliance?.insurance?.matchReason ||
      "Neither MC number nor carrier name matches this carrier. Upload a Certificate of Insurance (COI) under Compliance that matches the MC number or carrier name, then extract rates.";
  const ov = carrier.overview || {};

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-gray-50 dark:bg-acs-dark-surface">
      <div className="sticky top-0 z-10 w-full bg-white shadow-md dark:bg-acs-dark-surface">
        <StickyHeader
          mode={null}
          leftLabel="Carrier Profile"
          infoData={{
            Carrier: {
              label: "Carrier",
              value: carrier.name,
              readonly: true,
            },
            MC: {
              label: "MC / DOT",
              value: `${carrier.mc} · ${carrier.dot}`,
              readonly: true,
            },
            Status: {
              label: "Stage",
              value: carrier.stage,
              readonly: true,
            },
            Score: {
              label: "Score",
              value: carrier.score != null ? String(carrier.score) : "—",
              readonly: true,
            },
            Risk: {
              label: "Risk",
              value: carrier.risk || "—",
              readonly: true,
            },
          }}
          buttons={[
            {
              label: "Edit Profile",
              type: "secondary",
              onClick: () => onEditProfile?.(carrier),
            },
            {
              label: "Add Rate",
              type: "primary",
              onClick: () => onAddRate?.(carrier),
            },
            {
              label: "Log Activity",
              type: "secondary",
              onClick: () => onLogActivity?.(carrier.rawId, carrier.name),
            },
          ]}
        />
      </div>

      <ScrollbarWrapper className="min-h-0 w-full flex-1 overflow-y-auto bg-white p-fluid-1 dark:bg-acs-dark-surface">
        {error ? (
          <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-fluid-sm text-red-700">
            {error}
          </div>
        ) : null}

        <h3 className="mb-fluid-2 text-fluid-lg font-semibold text-gray-900 dark:text-acs-dark-text">
          Carrier Information
        </h3>
        <StaticSection title="Status at a Glance">
          <SectionGrid>
            <DisplayDetail label="Pipeline Stage" value={carrier.stage} />
            <DisplayDetail
              label="Carrier Score"
              value={carrier.score != null ? `${carrier.score} / 100` : null}
            />
            <DisplayDetail
              label="Risk"
              value={`${carrier.risk || "—"} (${carrier.riskScore ?? "—"})`}
            />
            <DisplayDetail
              label="Onboarding"
              value={`${carrier.onboardStepsDone || 0} / ${
                carrier.onboardStepsTotal || 5
              } steps`}
            />
            <DisplayDetail
              label="Active Rates"
              value={`${carrier.activeLanes ?? 0} lanes`}
            />
            <DisplayDetail label="Last Activity" value={carrier.lastActivity} />
          </SectionGrid>
        </StaticSection>

        <ExpandableHeader
          headerLabel="Carrier Identity"
          headerType="primary"
          icon={FileText}
          initiallyExpanded
        >
          <SectionGrid>
            <DisplayDetail label="Legal Name" value={carrier.name} />
            <DisplayDetail label="MC Number" value={carrier.mc} />
            <DisplayDetail label="DOT Number" value={carrier.dot} />
            <DisplayDetail label="Coverage / Regions" value={carrier.coverageLabel} />
            <DisplayDetail label="Fleet Size" value={carrier.fleetSize} />
            <DisplayDetail
              label="Equipment"
              value={(carrier.equipment || []).join(", ")}
            />
            <DisplayDetail label="Primary Contact" value={carrier.contact?.name} />
            <DisplayDetail label="Phone" value={carrier.contact?.phone} />
            <DisplayDetail label="Email" value={carrier.contact?.email} />
            <DisplayDetail label="Assigned Rep" value={carrier.assignedRepEmail} />
            <DisplayDetail label="Notes" value={carrier.notes} multiline className="xl:col-span-2" />
          </SectionGrid>
        </ExpandableHeader>

        <ExpandableHeader
          headerLabel="Overview"
          headerType="secondary"
          icon={FileText}
          initiallyExpanded
        >
          <SectionGrid>
            <DisplayDetail label="Current stage" value={ov.stageDetail || carrier.stage} />
            <DisplayDetail label="Open lanes" value={ov.openLanes} />
            <DisplayDetail label="Compliance status" value={ov.complianceStatus} />
            <DisplayDetail label="Initial onboarding" value={ov.onboardingStatus} />
          </SectionGrid>
          {carrier.recentActivity ? (
            <div className="border-t border-gray-200 bg-[#F9FAFE] p-fluid-3 dark:border-acs-dark-border dark:bg-acs-dark-surface">
              <h4 className="mb-fluid-2 text-fluid-sm font-semibold text-gray-700 dark:text-acs-dark-text">
                Most Recent Activity
              </h4>
              <TimelineItem {...carrier.recentActivity} />
            </div>
          ) : null}
        </ExpandableHeader>

        <ExpandableHeader
          headerLabel="Communication"
          headerType="tertiary"
          icon={MessageSquare}
          initiallyExpanded={false}
        >
          <div className="space-y-fluid-2 bg-[#F9FAFE] p-fluid-3 dark:bg-acs-dark-surface">
            <NoteFlag>
              Communication is grouped by rate/lane — each negotiation thread stays
              scoped to the lane it belongs to.
            </NoteFlag>
            <input
              value={commQuery}
              onChange={(e) => setCommQuery(e.target.value)}
              placeholder='Search communication (e.g. "fuel surcharge", "ATL")'
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-fluid-sm dark:border-acs-dark-border dark:bg-acs-dark-surface dark:text-acs-dark-text"
            />
            {threads.map((t) => (
              <div
                key={t.id}
                className="overflow-hidden rounded-md border border-gray-300 bg-white dark:border-acs-dark-border dark:bg-acs-dark-surface"
              >
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-white px-fluid-2 py-fluid-1.5 dark:border-acs-dark-border">
                  <span className="font-mono text-fluid-sm font-semibold text-[#223688]">
                    {t.laneName}
                  </span>
                  <span className="text-dense-xs text-gray-500">{t.meta}</span>
                </div>
                <div className="px-fluid-2 py-fluid-1">
                  {(t.messages || []).map((m, i) => (
                    <TimelineItem key={`${t.id}-${i}`} {...m} />
                  ))}
                </div>
              </div>
            ))}
            {!threads.length ? (
              <p className="text-fluid-sm text-gray-500">No communication threads yet.</p>
            ) : null}
          </div>
        </ExpandableHeader>

        <ExpandableHeader
          headerLabel="Rates & Negotiation"
          headerType="primary"
          icon={Route}
          initiallyExpanded={false}
        >
          <div className="space-y-fluid-3 bg-[#F9FAFE] p-fluid-3 dark:bg-acs-dark-surface">
            {!insuranceIdentityMatched ? (
              <div
                role="alert"
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-fluid-sm text-red-700"
              >
                {identityMatchError}
              </div>
            ) : null}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-fluid-sm text-gray-500">Rate Sheet</span>
              <div className="flex flex-wrap items-center gap-2">
                {(carrier.rateVersions || []).map((v) => (
                  <span
                    key={v.id}
                    className={`rounded-full border px-2 py-0.5 text-dense-xs ${
                      v.current
                        ? "border-[#223688] bg-[#223688] text-white"
                        : "border-gray-300 bg-white text-gray-600"
                    }`}
                  >
                    {v.label}
                  </span>
                ))}
                <Button
                  text="Add Rate"
                  onClick={() => onAddRate?.(carrier)}
                  backgroundColor="#223688"
                  textColor="white"
                  fontSize="14px"
                  padding="8px 16px"
                  borderRadius="24px"
                />
                <Button
                  text="Upload Rate Document"
                  variant="outline"
                  onClick={() => setUploadModal("rate")}
                />
              </div>
            </div>

            <div className="overflow-x-auto rounded-md border border-gray-300 bg-white">
              <table className="min-w-full border-collapse text-fluid-sm">
                <thead>
                  <tr className="bg-gray-100">
                    {["Rate Document", "Uploaded", "File Status", "Action"].map((h) => (
                      <th
                        key={h}
                        className="border-b border-gray-200 px-3 py-2 text-left text-dense-xs font-semibold text-gray-600"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(carrier.rateDocuments || []).map((d) => (
                    <tr key={d.id} className="border-b border-gray-200 last:border-0">
                      <td className="px-3 py-2 font-semibold">{d.name}</td>
                      <td className="px-3 py-2 font-mono">{d.uploaded}</td>
                      <td className="px-3 py-2">
                        <RateOnboardChip status={d.status} label={d.statusLabel} />
                      </td>
                      <td className="px-3 py-2">
                        {d.canStartExtraction && insuranceIdentityMatched ? (
                          <button
                            type="button"
                            disabled={extractingDocId === d.id}
                            onClick={() => handleStartExtraction(d)}
                            className="rounded-full border border-[#223688] px-3 py-1 text-dense-xs font-semibold text-[#223688] hover:bg-[#E8EFF3] disabled:opacity-50"
                          >
                            {extractingDocId === d.id
                              ? "Extracting…"
                              : d.apiStatus === "FAILED"
                                ? "Re-run Extraction"
                                : "Start Extraction"}
                          </button>
                        ) : d.canStartExtraction ? (
                          <span className="text-dense-xs font-medium text-red-600">
                            MC or carrier name must match
                          </span>
                        ) : (
                          <span className="text-dense-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!(carrier.rateDocuments || []).length ? (
                    <tr>
                      <td colSpan={4} className="px-3 py-6 text-center text-gray-500">
                        No rate documents uploaded yet.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            <LaneRatesGrid rates={carrier.laneRates || []} />
          </div>
        </ExpandableHeader>

        <ExpandableHeader
          headerLabel="Compliance"
          headerType="info"
          icon={Shield}
          initiallyExpanded={false}
        >
          {carrier.compliance ? (
            <div className="space-y-fluid-3 bg-[#F9FAFE] p-fluid-3 dark:bg-acs-dark-surface">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-fluid-sm text-gray-500">
                  Last refreshed: {carrier.compliance.lastRefreshed}
                </span>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    ref={insuranceFileInputRef}
                    type="file"
                    accept=".pdf,image/png,image/jpeg,image/jpg,image/webp,application/pdf"
                    className="hidden"
                    onChange={handleUploadInsurance}
                  />
                  <button
                    type="button"
                    disabled={uploadingInsurance}
                    onClick={() => insuranceFileInputRef.current?.click()}
                    className="rounded-full border border-[#223688] bg-white px-3 py-1.5 text-fluid-sm font-semibold text-[#223688] hover:bg-[#E8EFF3] disabled:opacity-50"
                  >
                    {uploadingInsurance
                      ? "Validating insurance…"
                      : "Upload Insurance (COI)"}
                  </button>
                  <button
                    type="button"
                    disabled={refreshingCompliance}
                    onClick={handleRefreshCompliance}
                    className="rounded-full border border-gray-300 px-3 py-1.5 text-fluid-sm font-semibold text-[#223688] hover:bg-gray-50 disabled:opacity-50"
                  >
                    {refreshingCompliance ? "Running…" : "Run Validation"}
                  </button>
                </div>
              </div>

              {carrier.compliance.insurance ? (
                <div className="rounded-md border border-gray-300 bg-white p-3">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <div className="text-fluid-sm font-semibold text-gray-800">
                      Insurance certificate
                    </div>
                    <span
                      className={`rounded-full px-2 py-0.5 text-dense-xs font-semibold ${
                        carrier.compliance.insurance.identityMatched === true
                          ? "bg-[#E4F4EB] text-[#2A7A54]"
                          : "bg-[#FBEAE8] text-[#B93B32]"
                      }`}
                    >
                      {carrier.compliance.insurance.identityMatched === true
                        ? "Identity matched"
                        : "Identity mismatch"}
                    </span>
                  </div>
                  <p className="mb-3 text-dense-xs text-gray-600">
                    {carrier.compliance.insurance.matchReason ||
                      "Upload a COI to validate MC / insured name against this carrier."}
                  </p>
                  <SectionGrid>
                    <DisplayDetail
                      label="Insured name"
                      value={carrier.compliance.insurance.insuredName}
                    />
                    <DisplayDetail
                      label="MC on certificate"
                      value={carrier.compliance.insurance.mcNumber}
                    />
                    <DisplayDetail
                      label="Policy number"
                      value={carrier.compliance.insurance.policyNumber}
                    />
                    <DisplayDetail
                      label="Insurer"
                      value={carrier.compliance.insurance.insurerName}
                    />
                    <DisplayDetail
                      label="Policy start"
                      value={carrier.compliance.insurance.policyStartDate}
                    />
                    <DisplayDetail
                      label="Policy end"
                      value={carrier.compliance.insurance.policyEndDate}
                    />
                    <DisplayDetail
                      label="File"
                      value={carrier.compliance.insurance.fileName}
                    />
                  </SectionGrid>
                </div>
              ) : (
                <NoteFlag>
                  Upload a Certificate of Insurance (PDF/image). We check that the
                  MC number or insured name matches this carrier, then extract and
                  store policy start/end dates.
                </NoteFlag>
              )}

              <SectionGrid>
                <DisplayDetail
                  label={
                    <span className="inline-flex items-center gap-1">
                      Risk Score
                      <RiskScoringInfo />
                    </span>
                  }
                  value={carrier.compliance.riskScore}
                />
                <DisplayDetail
                  label="Risk Tier"
                  value={carrier.compliance.riskTier}
                />
              </SectionGrid>
              <div className="grid gap-2 sm:grid-cols-2">
                {(carrier.compliance.checks || []).map((c) => (
                  <div
                    key={c.id || c.title}
                    className="rounded-md border border-gray-300 bg-white p-3"
                  >
                    <div className="flex items-center gap-2 text-fluid-sm font-semibold">
                      <span
                        className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] text-white ${
                          c.ok
                            ? "bg-[#2A7A54]"
                            : c.apiStatus === "FAIL"
                              ? "bg-[#AD2B2B]"
                              : "bg-[#B7660C]"
                        }`}
                      >
                        {c.ok ? "✓" : "!"}
                      </span>
                      {c.title}
                      {c.apiStatus ? (
                        <span className="ml-auto text-dense-xs font-medium text-gray-500">
                          {c.apiStatus}
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1 text-dense-xs text-gray-600">{c.detail}</p>
                    {c.needsManualVerify ? (
                      <button
                        type="button"
                        onClick={() => setVerifyCheck(c)}
                        className="mt-2 rounded-full border border-[#223688] px-3 py-1 text-dense-xs font-semibold text-[#223688] hover:bg-[#E8EFF3]"
                      >
                        Manual Verification
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-fluid-3 bg-[#F9FAFE] p-fluid-3 text-fluid-sm text-gray-500">
              <p>
                Compliance validation has not been run for this carrier yet. You
                can upload a COI first to validate insurance, or run full
                validation.
              </p>
              <div className="flex flex-wrap gap-2">
                <input
                  ref={insuranceFileInputRef}
                  type="file"
                  accept=".pdf,image/png,image/jpeg,image/jpg,image/webp,application/pdf"
                  className="hidden"
                  onChange={handleUploadInsurance}
                />
                <Button
                  text={
                    uploadingInsurance
                      ? "Validating insurance…"
                      : "Upload Insurance (COI)"
                  }
                  onClick={() => insuranceFileInputRef.current?.click()}
                  disabled={uploadingInsurance}
                  variant="outline"
                />
                <Button
                  text="Run Validation"
                  onClick={handleRefreshCompliance}
                  disabled={refreshingCompliance}
                />
              </div>
            </div>
          )}
        </ExpandableHeader>

        <ExpandableHeader
          headerLabel="Documents"
          headerType="secondary"
          icon={FolderOpen}
          initiallyExpanded={false}
        >
          <div className="space-y-fluid-3 bg-[#F9FAFE] p-fluid-3 dark:bg-acs-dark-surface">
            <NoteFlag>
              Single repository for this carrier — carrier-level docs (COI, W-9,
              MSA) and rate files uploaded under Rates & Negotiation.
            </NoteFlag>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-fluid-sm font-semibold text-gray-800 dark:text-acs-dark-text">
                Carrier-Level Documents
              </div>
              <Button
                text="+ Upload Document"
                onClick={() => setUploadModal("carrier")}
              />
            </div>

            {(carrier.documents?.carrierLevel || []).map((d) => (
              <div
                key={d.id || `${d.name}-${d.uploaded}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 dark:border-acs-dark-border dark:bg-acs-dark-surface"
              >
                <div className="min-w-0">
                  <div className="truncate text-fluid-sm font-semibold">
                    {d.name}
                  </div>
                  <div className="text-dense-xs text-gray-500">
                    {d.typeLabel}
                    {d.version != null ? ` · v${d.version}` : ""}
                    {d.uploaded && d.uploaded !== "—"
                      ? ` · ${d.uploaded}`
                      : ""}
                    {d.expiryDate ? ` · Expires ${d.expiryDate}` : ""}
                  </div>
                </div>
                <Chip tone={d.status === "received" ? "risk-low" : "risk-med"}>
                  {d.statusLabel}
                </Chip>
              </div>
            ))}
            {!(carrier.documents?.carrierLevel || []).length ? (
              <p className="text-fluid-sm text-gray-500">
                No carrier-level documents yet.
              </p>
            ) : null}

            <div className="pt-2 text-fluid-sm font-semibold text-gray-800 dark:text-acs-dark-text">
              Rate Documents
            </div>
            {(carrier.rateDocuments || []).map((d) => (
              <div
                key={d.id || d.name}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 dark:border-acs-dark-border dark:bg-acs-dark-surface"
              >
                <div className="min-w-0">
                  <div className="truncate text-fluid-sm font-semibold">
                    {d.name}
                  </div>
                  <div className="text-dense-xs text-gray-500">
                    {d.uploaded && d.uploaded !== "—"
                      ? `Uploaded ${d.uploaded}`
                      : "Rate document"}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <RateOnboardChip status={d.status} label={d.statusLabel} />
                  {d.canStartExtraction && insuranceIdentityMatched ? (
                    <button
                      type="button"
                      disabled={extractingDocId === d.id}
                      onClick={() => handleStartExtraction(d)}
                      className="rounded-full border border-[#223688] px-3 py-1 text-dense-xs font-semibold text-[#223688] hover:bg-[#E8EFF3] disabled:opacity-50"
                    >
                      {extractingDocId === d.id
                        ? "Extracting…"
                        : d.apiStatus === "FAILED"
                          ? "Re-run Extraction"
                          : "Start Extraction"}
                    </button>
                  ) : d.canStartExtraction ? (
                    <span className="max-w-[220px] text-right text-dense-xs font-medium text-red-600">
                      MC or carrier name must match
                    </span>
                  ) : null}
                </div>
              </div>
            ))}
            {!(carrier.rateDocuments || []).length ? (
              <p className="text-fluid-sm text-gray-500">
                No rate documents yet. Use Upload Rate Document under Rates
                & Negotiation.
              </p>
            ) : null}
          </div>
        </ExpandableHeader>

        <ExpandableHeader
          headerLabel="Onboarding"
          headerType="success"
          icon={ListChecks}
          initiallyExpanded={false}
        >
          <div className="space-y-fluid-3 bg-[#F9FAFE] p-fluid-3">
            <div className="flex items-center gap-3">
              <OnboardProgress
                done={carrier.onboardStepsDone}
                total={carrier.onboardStepsTotal}
              />
              <RiskChip risk={carrier.risk || "Low"} />
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
              {(carrier.carrierOnboarding || []).map((s, idx) => (
                <div
                  key={s.id || s.title}
                  className="rounded-md border border-gray-300 bg-white p-3 text-center"
                >
                  <div
                    className={`mx-auto mb-2 flex h-8 w-8 items-center justify-center rounded-full text-fluid-sm font-bold text-white ${
                      s.state === "done"
                        ? "bg-[#2A7A54]"
                        : s.state === "active"
                          ? "bg-[#223688]"
                          : "bg-gray-300 text-gray-600"
                    }`}
                  >
                    {s.state === "done" ? "✓" : idx + 1}
                  </div>
                  <div className="text-fluid-sm font-semibold">{s.title}</div>
                  <div className="text-dense-xs text-gray-500">{s.detail}</div>
                </div>
              ))}
            </div>
          </div>
        </ExpandableHeader>
      </ScrollbarWrapper>

      {uploadModal ? (
        <UploadDocumentModal
          carrierName={carrier.name}
          kind={uploadModal}
          onClose={() => setUploadModal(null)}
          onUpload={handleUploadDocuments}
        />
      ) : null}

      {verifyCheck ? (
        <ManualVerifyModal
          checkTitle={verifyCheck.title}
          onClose={() => setVerifyCheck(null)}
          onConfirm={handleManualVerify}
        />
      ) : null}
    </div>
  );
}
